import { useCallback, useEffect, useMemo, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import {
  AlertTriangle, ArrowDown, ArrowUp, Check, ChevronDown, ChevronRight, FlaskConical, Loader2, Plus, Route, Search, Trash2, X,
} from 'lucide-react';
import {
  DEFAULT_INGEST_RULE_THRESHOLD,
  describeIngestConditions,
  describeIngestRules,
  INGEST_RULE_LIMITS,
  INGEST_MAIL_CLASS_LABELS,
  INGEST_MAIL_CLASSES,
  INGEST_RULE_SOURCE_LABELS,
  INGEST_RULE_SOURCE_TYPES,
  normalizeIngestPolicy,
  type IngestMailClass,
  type IngestRule,
  type IngestRuleConditions,
  type IngestRulePolicy,
  type IngestRuleSourceType,
} from '@repo/shared-types';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input, Select, Textarea } from '../components/ui/input';
import { api, type IngestRuleOptions, type IngestRuleScope, type IngestRuleTestResult } from '../lib/api';
import { cn, timeAgo } from '../lib/utils';

/** The router's key in the maps below — the one scope with no project id. */
const ROUTER = 'router';
const keyOf = (scope: { projectId: string | null }) => scope.projectId ?? ROUTER;

function samePolicy(a: IngestRulePolicy, b: IngestRulePolicy): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** A fresh rule id unique within one policy — kept by the server once saved. */
function newRuleId(policy: IngestRulePolicy): string {
  const used = new Set(policy.rules.map((r) => r.id));
  for (let n = policy.rules.length + 1; ; n++) if (!used.has(`r${n}`)) return `r${n}`;
}

function blankRule(policy: IngestRulePolicy): IngestRule {
  return { id: newRuleId(policy), name: '', effect: policy.defaultEffect === 'block' ? 'allow' : 'block', when: {}, ask: null };
}

/**
 * Ingest rules — what the Organization router, and each project, refuses to take in.
 *
 * Every scope on one page, the router pinned first and the projects in the hierarchy's order, because
 * a rule set is built across the hierarchy: "only Thrasos meetings into Thrasos" and "no recruiter
 * mail anywhere" are one decision made in one place. Edits are held locally and saved together —
 * **Save all** writes every changed scope as a new version in one request, all or nothing, like
 * Feature Permissions. Tick several projects to add one rule to each, or copy one project's rules.
 *
 * What the rules do is enforced by code at three gates (see `docs/ingest-rules.md`); the agent is
 * told them and cannot lift them.
 */
export default function IngestRules() {
  const [scopes, setScopes] = useState<IngestRuleScope[]>([]);
  const [options, setOptions] = useState<IngestRuleOptions | null>(null);
  const [drafts, setDrafts] = useState<Map<string, IngestRulePolicy>>(new Map());
  const [open, setOpen] = useState<Set<string>>(new Set([ROUTER]));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [filter, setFilter] = useState('');
  const [onlyRuled, setOnlyRuled] = useState(false);
  const [composer, setComposer] = useState<IngestRule | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, opts] = await Promise.all([api.ingestRules.list(), api.ingestRules.options()]);
      setScopes(list.scopes);
      setOptions(opts);
      setDrafts(new Map(list.scopes.map((s) => [keyOf(s), s.policy])));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const byKey = useMemo(() => new Map(scopes.map((s) => [keyOf(s), s])), [scopes]);
  const changed = useMemo(
    () => scopes.filter((s) => {
      const draft = drafts.get(keyOf(s));
      return draft !== undefined && !samePolicy(draft, s.policy);
    }),
    [scopes, drafts],
  );

  const setDraft = (key: string, next: IngestRulePolicy) => {
    setSavedAt(null);
    setDrafts((prev) => new Map(prev).set(key, next));
  };

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return scopes.filter((s) => {
      if (q && !`${s.name} ${s.path}`.toLowerCase().includes(q)) return false;
      if (onlyRuled && s.projectId !== null) {
        const draft = drafts.get(keyOf(s)) ?? s.policy;
        if (draft.rules.length === 0 && draft.defaultEffect === 'allow') return false;
      }
      return true;
    });
  }, [scopes, filter, onlyRuled, drafts]);

  const editableSelected = [...selected].filter((key) => byKey.get(key)?.canEdit);

  const save = async () => {
    setProblems([]);
    // Validated here with the function the server uses, so a refusal reads the same either way.
    const local = changed.flatMap((s) => {
      const result = normalizeIngestPolicy(drafts.get(keyOf(s)));
      return result.ok ? [] : result.problems.map((p) => `${s.name}: ${p}`);
    });
    if (local.length > 0) {
      setProblems(local);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.ingestRules.save(
        changed.map((s) => ({ projectId: s.projectId, baseVersion: s.version, policy: drafts.get(keyOf(s))! })),
      );
      await load();
      setSavedAt(Date.now());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const addToSelected = (rule: IngestRule) => {
    for (const key of editableSelected) {
      const draft = drafts.get(key)!;
      // The same rule written into each project's own policy, so each can later diverge.
      setDraft(key, { ...draft, rules: [...draft.rules, { ...rule, id: newRuleId(draft) }] });
    }
    setComposer(null);
    setOpen((prev) => new Set([...prev, ...editableSelected]));
  };

  const copyFrom = (sourceKey: string) => {
    const source = drafts.get(sourceKey);
    if (!source) return;
    for (const key of editableSelected) {
      if (key === sourceKey) continue;
      setDraft(key, { ...source, rules: source.rules.map((r) => ({ ...r })) });
    }
    setOpen((prev) => new Set([...prev, ...editableSelected]));
  };

  return (
    <Layout title="Ingest rules" subtitle="What the router, and each project, refuses to take in">
      <p className="mb-4 max-w-3xl text-xs text-ink-muted">
        Rules judge what connectors file on their own — email, Slack, meetings, Drive files and the documents they carry.
        Anything a person files on purpose is never judged. The <strong>Organization router</strong> sees everything that
        arrives with no destination, before it is placed; a project sees everything headed into it. The first rule that
        matches decides. A held item stays in the Feed as <em>Held by rule</em>, and can be released.
      </p>

      <div className="sticky top-0 z-10 -mx-8 mb-4 flex flex-wrap items-center gap-2 border-b border-border bg-surface px-8 py-2">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find a project" className="h-8 w-56 pl-8 text-xs" />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-ink-muted">
          <input type="checkbox" checked={onlyRuled} onChange={(e) => setOnlyRuled(e.target.checked)} />
          Only scopes with rules
        </label>
        <div className="flex-1" />
        {editableSelected.length > 0 && (
          <>
            <span className="text-xs text-ink-muted">{editableSelected.length} selected</span>
            <Button size="sm" onClick={() => setComposer(blankRule({ context: '', defaultEffect: 'allow', rules: [] }))}>
              <Plus size={12} /> Add rule to selected
            </Button>
            <Select
              value=""
              onChange={(e) => e.target.value && copyFrom(e.target.value)}
              className="h-8 w-48 text-xs"
              aria-label="Copy rules from"
            >
              <option value="">Copy rules from…</option>
              {scopes
                .filter((s) => (drafts.get(keyOf(s)) ?? s.policy).rules.length > 0)
                .map((s) => <option key={keyOf(s)} value={keyOf(s)}>{s.name}</option>)}
            </Select>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
          </>
        )}
        {savedAt && changed.length === 0 && (
          <span className="flex items-center gap-1 text-xs text-success-text"><Check size={12} /> Saved</span>
        )}
        <Button variant="primary" size="sm" disabled={changed.length === 0 || saving} onClick={() => void save()}>
          {saving ? <Loader2 size={12} className="animate-spin" /> : null}
          Save all{changed.length > 0 ? ` (${changed.length})` : ''}
        </Button>
        {changed.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => setDrafts(new Map(scopes.map((s) => [keyOf(s), s.policy])))}>
            Discard
          </Button>
        )}
      </div>

      {error && <Banner tone="danger" text={error} onClose={() => setError(null)} />}
      {problems.length > 0 && (
        <div className="mb-4 rounded-md border border-danger-ring bg-danger-bg px-3 py-2 text-xs text-danger-text">
          <div className="mb-1 font-medium">Nothing was saved:</div>
          <ul className="list-disc pl-4">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
        </div>
      )}

      {composer && (
        <div className="mb-4 rounded-lg border border-border bg-card p-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-medium text-ink">
            One rule, added to the end of each of {editableSelected.length} project{editableSelected.length === 1 ? '' : 's'}
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={() => setComposer(null)}><X size={12} /></Button>
          </div>
          <RuleEditor rule={composer} options={options} onChange={setComposer} canEdit />
          <div className="mt-2 flex justify-end">
            <Button size="sm" variant="primary" disabled={!composer.name.trim()} onClick={() => addToSelected(composer)}>
              Add to {editableSelected.length} project{editableSelected.length === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-ink-muted"><Loader2 size={14} className="animate-spin" /> Loading rules…</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {visible.map((scope) => {
            const key = keyOf(scope);
            const draft = drafts.get(key) ?? scope.policy;
            return (
              <ScopeRow
                key={key}
                scope={scope}
                draft={draft}
                dirty={!samePolicy(draft, scope.policy)}
                expanded={open.has(key)}
                selected={selected.has(key)}
                options={options}
                onToggle={() => setOpen((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; })}
                onSelect={(on) => setSelected((prev) => { const next = new Set(prev); if (on) next.add(key); else next.delete(key); return next; })}
                onChange={(next) => setDraft(key, next)}
              />
            );
          })}
          {visible.length === 0 && <div className="px-4 py-6 text-center text-xs text-ink-faint">No scope matches.</div>}
        </div>
      )}
    </Layout>
  );
}

function Banner({ tone, text, onClose }: { tone: 'danger'; text: string; onClose: () => void }) {
  return (
    <div className={cn('mb-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs', tone === 'danger' && 'border-danger-ring bg-danger-bg text-danger-text')}>
      <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
      <span className="flex-1">{text}</span>
      <button onClick={onClose}><X size={12} /></button>
    </div>
  );
}

/** One scope: a summary line, and in place beneath it, the rule list. */
function ScopeRow({
  scope, draft, dirty, expanded, selected, options, onToggle, onSelect, onChange,
}: {
  scope: IngestRuleScope;
  draft: IngestRulePolicy;
  dirty: boolean;
  expanded: boolean;
  selected: boolean;
  options: IngestRuleOptions | null;
  onToggle: () => void;
  onSelect: (on: boolean) => void;
  onChange: (next: IngestRulePolicy) => void;
}) {
  const router = scope.projectId === null;
  const summary = describeIngestRules(draft);
  const [test, setTest] = useState<IngestRuleTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  const runTest = async () => {
    setTesting(true);
    setTestError(null);
    try {
      setTest(await api.ingestRules.test(scope.projectId, draft));
    } catch (e) {
      setTestError((e as Error).message);
    } finally {
      setTesting(false);
    }
  };

  const updateRule = (index: number, rule: IngestRule) => onChange({ ...draft, rules: draft.rules.map((r, i) => (i === index ? rule : r)) });
  const move = (index: number, by: -1 | 1) => {
    const rules = [...draft.rules];
    const [rule] = rules.splice(index, 1);
    rules.splice(index + by, 0, rule!);
    onChange({ ...draft, rules });
  };

  return (
    <div className={cn('border-b border-border last:border-b-0', router && 'bg-surface-subtle')}>
      <div className="flex items-center gap-2.5 px-4 py-2.5" style={{ paddingLeft: router ? 16 : 16 + scope.depth * 20 }}>
        <input
          type="checkbox"
          checked={selected}
          disabled={!scope.canEdit || router}
          onChange={(e) => onSelect(e.target.checked)}
          aria-label={`Select ${scope.name}`}
          className={cn(router && 'invisible')}
        />
        <button onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
          {expanded ? <ChevronDown size={16} className="text-ink-faint" /> : <ChevronRight size={16} className="text-ink-faint" />}
          {router && <Route size={16} className="text-orange-500" />}
          <span className={cn('truncate text-sm', router ? 'font-semibold text-ink' : 'font-medium text-ink-secondary')}>{scope.name}</span>
          {dirty && <span className="text-xs font-medium text-orange-600">edited</span>}
          <span className="ml-2 truncate text-xs text-ink-faint">{summary ?? (router ? 'Accepts everything' : 'No rules — accepts what the router lets through')}</span>
        </button>
        <Badge variant={draft.defaultEffect === 'block' ? 'warning' : 'muted'}>{draft.defaultEffect === 'block' ? 'accepts only' : 'accepts'}</Badge>
        <span className="w-16 text-right text-xs text-ink-faint">{draft.rules.length} rule{draft.rules.length === 1 ? '' : 's'}</span>
        <span className="w-24 text-right text-xs text-ink-faint" title="Items its rules held in the last 7 days and nobody released">
          {scope.held7d > 0 ? <span className="text-warning-text">{scope.held7d} held · 7d</span> : '—'}
        </span>
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-border bg-surface px-5 py-4" style={{ paddingLeft: router ? 20 : 20 + scope.depth * 18 }}>
          {!scope.canEdit && (
            <div className="text-sm text-ink-faint">
              {router ? 'Only a system admin can change the Organization router.' : 'Read-only — you are not an admin of this project.'}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-[1fr_280px]">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-ink-secondary">What this scope is for — put in front of every question</span>
              <Textarea
                value={draft.context}
                disabled={!scope.canEdit}
                onChange={(e) => onChange({ ...draft, context: e.target.value })}
                rows={3}
                maxLength={INGEST_RULE_LIMITS.contextChars}
                placeholder={router ? 'A games consultancy. Client engagements, partner and vendor work, internal operations.' : 'What belongs in this project'}
                className="min-h-0"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-ink-secondary">When no rule matches</span>
              <Select
                value={draft.defaultEffect}
                disabled={!scope.canEdit}
                onChange={(e) => onChange({ ...draft, defaultEffect: e.target.value as 'allow' | 'block' })}
              >
                <option value="allow">Accept it</option>
                <option value="block">Refuse it (accept only what a rule allows)</option>
              </Select>
            </label>
          </div>

          {draft.rules.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-ink-faint">No rules yet — everything is accepted. Add one below.</div>
          ) : (
            <ol className="space-y-3">
              {draft.rules.map((rule, index) => (
                <li key={rule.id} className="flex gap-3 rounded-lg border border-border bg-card p-4">
                  <span className="mt-1.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-muted text-xs font-semibold text-ink-muted">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <RuleEditor rule={rule} options={options} canEdit={scope.canEdit} onChange={(next) => updateRule(index, next)} />
                  </div>
                  {scope.canEdit && (
                    <div className="flex flex-col gap-1">
                      <IconButton disabled={index === 0} onClick={() => move(index, -1)} title="Earlier — the first match decides"><ArrowUp size={16} /></IconButton>
                      <IconButton disabled={index === draft.rules.length - 1} onClick={() => move(index, 1)} title="Later"><ArrowDown size={16} /></IconButton>
                      <IconButton danger onClick={() => onChange({ ...draft, rules: draft.rules.filter((_, i) => i !== index) })} title="Remove"><Trash2 size={16} /></IconButton>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {scope.canEdit && (
              <Button size="sm" disabled={draft.rules.length >= INGEST_RULE_LIMITS.rules} onClick={() => onChange({ ...draft, rules: [...draft.rules, blankRule(draft)] })}>
                <Plus size={12} /> Add rule
              </Button>
            )}
            <Button size="sm" disabled={testing} onClick={() => void runTest()}>
              {testing ? <Loader2 size={12} className="animate-spin" /> : <FlaskConical size={12} />} Test
            </Button>
            <span className="text-xs text-ink-faint">
              {scope.version ? `Version ${scope.version}${scope.updatedAt ? `, saved ${timeAgo(scope.updatedAt)}` : ''}` : 'Never saved'}
            </span>
          </div>
          {testError && <div className="text-sm text-danger-text">{testError}</div>}
          {test && <TestResult result={test} onClose={() => setTest(null)} />}
        </div>
      )}
    </div>
  );
}

/** One rule: effect and name, the conditions, and an optional question. */
function RuleEditor({
  rule, options, canEdit, onChange,
}: {
  rule: IngestRule;
  options: IngestRuleOptions | null;
  canEdit: boolean;
  onChange: (next: IngestRule) => void;
}) {
  const setWhen = (patch: Partial<IngestRuleConditions>) => {
    const when = { ...rule.when, ...patch };
    for (const k of Object.keys(when) as Array<keyof IngestRuleConditions>) {
      // `hasAttachment: false` is a condition, not an empty one.
      if (k === 'hasAttachment') {
        if (when.hasAttachment === undefined) delete when.hasAttachment;
      } else if (!(when[k] as unknown[] | undefined)?.length) delete when[k];
    }
    onChange({ ...rule, when });
  };
  const toggleIn = <T extends string>(key: 'mailClasses' | 'directions', value: T) => {
    const current = (rule.when[key] ?? []) as string[];
    setWhen({ [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value] } as Partial<IngestRuleConditions>);
  };
  const toggleType = (type: IngestRuleSourceType) => {
    const current = rule.when.sourceTypes ?? [];
    setWhen({ sourceTypes: current.includes(type) ? current.filter((t) => t !== type) : [...current, type] });
  };
  const chips = describeIngestConditions(rule.when);
  const summary = `${rule.effect === 'block' ? 'Holds' : 'Accepts'} ${chips.length ? chips.join(', ') : 'everything'}${rule.ask ? ', when the question says yes' : ''}`;
  // The mail conditions only mean something for mail; shown whenever mail is in scope.
  const types = rule.when.sourceTypes ?? [];
  const mailInScope = types.length === 0 || types.includes('EMAIL') || types.includes('LINKED_DOCUMENT');

  if (!canEdit) {
    return (
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={rule.effect === 'block' ? 'danger' : 'success'}>{rule.effect === 'block' ? 'Block' : 'Allow'}</Badge>
          <span className="text-sm font-medium text-ink">{rule.name || 'Unnamed rule'}</span>
        </div>
        <p className="text-sm text-ink-muted">{summary}</p>
        {rule.ask && <p className="text-sm text-ink-muted">Asks: “{rule.ask.question}” — yes at {rule.ask.threshold}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-border-strong p-0.5" role="group" aria-label="Effect">
            {(['block', 'allow'] as const).map((effect) => (
              <button
                key={effect}
                type="button"
                onClick={() => onChange({ ...rule, effect })}
                className={cn(
                  'rounded px-3 py-1.5 text-sm font-medium transition-colors',
                  rule.effect === effect
                    ? effect === 'block' ? 'bg-danger-bg text-danger-text' : 'bg-success-bg text-success-text'
                    : 'text-ink-muted hover:text-ink',
                )}
              >
                {effect === 'block' ? 'Block' : 'Allow'}
              </button>
            ))}
          </div>
          <Input
            value={rule.name}
            onChange={(e) => onChange({ ...rule, name: e.target.value })}
            placeholder="Name — what the agent and the Feed will call it"
            maxLength={INGEST_RULE_LIMITS.nameChars}
            className="min-w-[240px] flex-1 font-medium"
          />
        </div>
        <p className="text-xs text-ink-muted">{summary}</p>
      </div>

      <RuleSection title="Applies to">
        <div className="flex flex-wrap items-center gap-1.5">
          {INGEST_RULE_SOURCE_TYPES.map((type) => (
            <Pill key={type} on={types.includes(type)} onClick={() => toggleType(type)}>
              {INGEST_RULE_SOURCE_LABELS[type]}
            </Pill>
          ))}
          {types.length === 0 && <span className="ml-1 text-sm text-ink-faint">Any source</span>}
        </div>
      </RuleSection>

      <RuleSection title="Narrow it down" hint="Optional. Every field you fill must hold.">
        <div className="grid gap-3 md:grid-cols-2">
          <ListField
            label="From these sources"
            values={rule.when.sources ?? []}
            onChange={(sources) => setWhen({ sources })}
            suggestions={options?.sources.map((s) => ({ value: s.source, hint: `${s.count} · ${INGEST_RULE_SOURCE_LABELS[s.sourceType as IngestRuleSourceType] ?? s.sourceType}` })) ?? []}
            placeholder="slack:C08ENG, mail:brooke@…"
          />
          <ListField label="Title contains" values={rule.when.titleContains ?? []} onChange={(titleContains) => setWhen({ titleContains })} placeholder="OOO, Invoice" />
        </div>
      </RuleSection>

      {mailInScope && (
        <RuleSection title="Email" hint="Only ever matches mail, and the documents attached to it.">
          <div className="space-y-3">
            <PillRow label="Kind of mail">
              {INGEST_MAIL_CLASSES.map((value: IngestMailClass) => (
                <Pill key={value} on={rule.when.mailClasses?.includes(value) ?? false} onClick={() => toggleIn('mailClasses', value)}>
                  {INGEST_MAIL_CLASS_LABELS[value]}
                </Pill>
              ))}
            </PillRow>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <PillRow label="Direction">
                {(['inbound', 'sent'] as const).map((value) => (
                  <Pill key={value} on={rule.when.directions?.includes(value) ?? false} onClick={() => toggleIn('directions', value)}>
                    {value === 'sent' ? 'Sent by the mailbox' : 'Received'}
                  </Pill>
                ))}
              </PillRow>
              <PillRow label="Attachments">
                {([[undefined, 'Any'], [true, 'With'], [false, 'Without']] as const).map(([value, label]) => (
                  <Pill key={label} on={rule.when.hasAttachment === value} onClick={() => setWhen({ hasAttachment: value })}>
                    {label}
                  </Pill>
                ))}
              </PillRow>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <ListField label="Sent by" values={rule.when.senders ?? []} onChange={(senders) => setWhen({ senders: senders.map((v) => v.toLowerCase()) })} placeholder="justin@board.fun, no-reply*" />
              <ListField
                label="Sender domain"
                values={rule.when.senderDomains ?? []}
                onChange={(senderDomains) => setWhen({ senderDomains })}
                suggestions={options?.senderDomains.map((d) => ({ value: d.domain, hint: String(d.count) })) ?? []}
                placeholder="agency.com"
              />
              <ListField label="To or Cc includes" values={rule.when.recipients ?? []} onChange={(recipients) => setWhen({ recipients: recipients.map((v) => v.toLowerCase()) })} placeholder="ops@cdrexdev.com, *@board.fun" />
              <ListField label="Mail label" values={rule.when.labels ?? []} onChange={(labels) => setWhen({ labels })} placeholder="Label_7" />
            </div>
          </div>
        </RuleSection>
      )}

      <RuleSection title="Question" hint="Optional. A yes/no the screener answers about the text; the rule applies only on a yes.">
        <div className="flex flex-wrap items-start gap-3">
          <Textarea
            value={rule.ask?.question ?? ''}
            onChange={(e) =>
              onChange({
                ...rule,
                ask: e.target.value
                  ? { ...(rule.ask ?? { threshold: DEFAULT_INGEST_RULE_THRESHOLD }), question: e.target.value }
                  : null,
              })
            }
            placeholder="e.g. Is this a recruiter or job-board email?"
            maxLength={INGEST_RULE_LIMITS.questionChars}
            rows={2}
            className="min-h-0 min-w-[280px] flex-1"
          />
          {rule.ask && (
            <label className="flex items-center gap-2 pt-2 text-sm text-ink-muted">
              Counts as yes at
              <Input
                type="number"
                min={0.5}
                max={0.99}
                step={0.01}
                value={rule.ask.threshold}
                onChange={(e) => onChange({ ...rule, ask: { ...rule.ask!, threshold: Number(e.target.value) } })}
                className="w-20"
              />
            </label>
          )}
        </div>
      </RuleSection>
    </div>
  );
}

function RuleSection({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="border-t border-border pt-3">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">{title}</h4>
        {hint && <span className="text-xs text-ink-faint">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function PillRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 w-28 shrink-0 text-sm text-ink-muted">{label}</span>
      {children}
    </div>
  );
}

function Pill({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'rounded-full border px-3 py-1 text-sm transition-colors',
        on
          ? 'border-orange-400 bg-orange-50 font-medium text-orange-700 dark:bg-orange-950 dark:text-orange-300'
          : 'border-border-strong text-ink-secondary hover:border-ink-faint hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function IconButton({ danger, className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean }) {
  return (
    <button
      type="button"
      className={cn(
        'flex size-8 items-center justify-center rounded-md text-ink-faint transition-colors hover:bg-surface-muted disabled:pointer-events-none disabled:opacity-30',
        danger ? 'hover:text-danger-text' : 'hover:text-ink',
        className,
      )}
      {...props}
    />
  );
}

/** A list of short values typed comma-separated, with suggestions from what really arrived. */
function ListField({
  label, values, onChange, suggestions = [], placeholder,
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  suggestions?: Array<{ value: string; hint: string }>;
  placeholder: string;
}) {
  const [text, setText] = useState('');
  const id = `list-${label.replace(/\W+/g, '-')}`;
  const add = (raw: string) => {
    const next = raw.split(',').map((v) => v.trim()).filter(Boolean);
    if (next.length) onChange([...new Set([...values, ...next])]);
    setText('');
  };
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-ink-secondary">{label}</span>
      <div className="flex min-h-[40px] flex-wrap items-center gap-1.5 rounded-md border border-border-strong bg-card px-2 py-1.5 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-400/40">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-md bg-surface-muted px-2 py-0.5 text-sm text-ink-secondary">
            {v}
            <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter((x) => x !== v))} className="text-ink-faint hover:text-ink">
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          list={suggestions.length ? id : undefined}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add(text);
            }
          }}
          onBlur={() => text && add(text)}
          placeholder={values.length ? '' : placeholder}
          className="min-w-[140px] flex-1 bg-transparent px-1 text-sm text-ink outline-none placeholder:text-ink-faint"
        />
        {suggestions.length > 0 && (
          <datalist id={id}>
            {suggestions.map((s) => <option key={s.value} value={s.value}>{s.hint}</option>)}
          </datalist>
        )}
      </div>
    </label>
  );
}

/** What a draft would change, over the scope's last 100 automatic arrivals. */
function TestResult({ result, onClose }: { result: IngestRuleTestResult; onClose: () => void }) {
  const verdict = (v: IngestRuleTestResult['changed'][number]['live']) =>
    v.status === 'PASSED' ? <span className="text-success-text">accepted</span> : (
      <span className={v.status === 'UNANSWERED' ? 'text-warning-text' : 'text-danger-text'}>
        {v.status === 'UNANSWERED' ? 'unanswered' : 'held'}{v.ruleName ? ` · ${v.ruleName}` : ' · default'}
        {v.probability !== null ? ` (${Math.round(v.probability * 100)}%)` : ''}
      </span>
    );
  return (
    <div className="rounded-md border border-border bg-card p-3 text-[11px]">
      <div className="mb-2 flex items-center gap-2">
        <span className="font-medium text-ink">
          {result.checked} recent arrival{result.checked === 1 ? '' : 's'}: {result.blockedLive} held now, {result.blockedDraft} with this draft
          {' · '}{result.changed.length} would change
        </span>
        <span className="text-ink-faint">{result.jevCalls} Jev call{result.jevCalls === 1 ? '' : 's'} (cached answers cost none)</span>
        <div className="flex-1" />
        <button onClick={onClose} className="text-ink-faint hover:text-ink"><X size={12} /></button>
      </div>
      {result.errors.length > 0 && <div className="mb-2 text-warning-text">Jev: {result.errors[0]}</div>}
      {result.changed.length === 0 ? (
        <div className="text-ink-faint">No verdict changes.</div>
      ) : (
        <table className="w-full">
          <thead className="text-left text-ink-faint">
            <tr><th className="py-1 font-medium">Arrival</th><th className="font-medium">Now</th><th className="font-medium">With the draft</th></tr>
          </thead>
          <tbody>
            {result.changed.map((row) => (
              <tr key={row.feedItemId} className="border-t border-border">
                <td className="max-w-[340px] truncate py-1 pr-2 text-ink-secondary" title={row.source}>
                  {row.title} <span className="text-ink-faint">· {timeAgo(row.capturedAt)}</span>
                </td>
                <td className="pr-2">{verdict(row.live)}</td>
                <td>{verdict(row.draft)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-2 text-ink-faint">Nothing is saved by a test.</div>
    </div>
  );
}
